import { Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Chip, IconButton, Menu, MenuItem, InputLabel, Tooltip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState, useRef } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest, formatCanadianPrice } from "GlobalFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { FaSquareCheck } from "react-icons/fa6";
import { FaRegSquare } from "react-icons/fa";
import AddProduct from "./AddProduct";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AlertDialog from "elements/AlertDialog";
import AddCreditCard from "./AddCreditCard";
import CancelSubscriptionDialog from "./components/CancelSubscriptionDialog";
import ResumeSubscriptionDialog from "./components/ResumeSubscriptionDialog";
import { SlArrowRight } from "react-icons/sl";
import EventLogs from "elements/EventLogs";
import { Dropdown, Form } from 'semantic-ui-react';
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { IoMdCheckmark, IoMdClose } from "react-icons/io";
import { CiCircleAlert } from "react-icons/ci";

export default function SubscriptionEditor(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { subscription_id } = useParams();
  const [subscription, setSubscription] = useState<any>({});
  const [payments, setPayments] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedPayments, setSelectedPayments] = useState<{subscription_id: string, subscription_product_id: number, subscription_payment_id: number}[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeProduct, setActiveProduct] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [openCreditCard, setOpenCreditCard] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openResumeDialog, setOpenResumeDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState<number | null>(null); 
  const [sendingMultipleInvoices, setSendingMultipleInvoices] = useState(false);

  const [status, setStatus] = useState("active");
  const [editStatus, setEditStatus] = useState(false);
  const [originalClientData, setOriginalClientData] = useState({
    client_name: '',
    client_lastname: '',
    email: ''
  });
  const [eventsKey, setEventsKey] = useState(0); // Key to force EventLogs refresh
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const fetchSubscriptionDetails = () => {
    setLoading(true);
    ApiRequest({
      method: "GET",
      url: "/subscriptions/details",
      query: { subscription_id },
      setResponse: (response: any) => {
        setSubscription(response.data);
        setPayments(response.data.payments || []);
        setProducts(response.data.products || []);
        setStatus(response.data.status || "active");
        // Save original client data for cancel functionality
        setOriginalClientData({
          client_name: response.data.client_name || '',
          client_lastname: response.data.client_lastname || '',
          email: response.data.email || ''
        });
        setLoading(false);
      },
    });
  };

  const handleSubscriptionUpdate = () => {
    fetchSubscriptionDetails();
    setEventsKey(prev => prev + 1); // Force EventLogs to refresh
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [subscription_id]);

  const handleSubmit = () => {
    setLoading(true);
    ApiRequest({
      method: "POST",
      url: "/subscriptions/addedit",
      query: subscription,
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar("Subscription saved successfully", { variant: "success" });
          navigate("/subscriptions");
        } else {
          enqueueSnackbar(response.message, { variant: "error" });
        }
      },
    });
  };

  const handleUpdateClientInfo = () => {
    setLoading(true);
    ApiRequest({
      method: "POST",
      url: "/subscriptions/updateClientInfo",
      body: {
        subscription_id,
        client_name: subscription.client_name,
        client_lastname: subscription.client_lastname,
        email: subscription.email
      },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar("Client information updated successfully", { variant: "success" });
          // Update original data with new values
          setOriginalClientData({
            client_name: subscription.client_name || '',
            client_lastname: subscription.client_lastname || '',
            email: subscription.email || ''
          });
        } else {
          enqueueSnackbar(response.message || "Failed to update client information", { variant: "error" });
        }
      },
    });
  };

  const handleCancelEdit = () => {
    // Restore original values
    setSubscription((prev: any) => ({
      ...prev,
      client_name: originalClientData.client_name,
      client_lastname: originalClientData.client_lastname,
      email: originalClientData.email
    }));
    setEditStatus(false);
  };

  const handleSelectAll = (event: React.MouseEvent) => {
    if (selectedPayments.length < payments.length) {
      // Select all payments
      const allPayments = payments.map(payment => ({
        subscription_id: subscription_id as string,
        subscription_product_id: payment.product_ids[0],
        subscription_payment_id: payment.subscription_payment_id
      }));
      setSelectedPayments(allPayments);
    } else {
      setSelectedPayments([]);
    }
  };

  const handleSelectOne = (payment: any) => {
    const paymentObject = {
      subscription_id: subscription_id as string,
      subscription_product_id: payment.product_ids[0],
      subscription_payment_id: payment.subscription_payment_id
    };
    
    // Check if this payment is already selected
    const isSelected = selectedPayments.some(
      item => item.subscription_id === paymentObject.subscription_id && 
             item.subscription_product_id === paymentObject.subscription_product_id &&
             item.subscription_payment_id === paymentObject.subscription_payment_id
    );
    
    if (isSelected) {
      // Remove from selection
      setSelectedPayments(selectedPayments.filter(
        item => !(item.subscription_id === paymentObject.subscription_id && 
                item.subscription_product_id === paymentObject.subscription_product_id &&
                item.subscription_payment_id === paymentObject.subscription_payment_id)
      ));
    } else {
      // Add to selection
      setSelectedPayments([...selectedPayments, paymentObject]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: any) => {
    setMenuAnchor(event.currentTarget);
    setActiveProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditProduct = () => {
    setIsEditing(true);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeletePrompt = () => {
    setOpenAlert(true);
    handleMenuClose();
  };

  const handleDeleteProduct = () => {
    setLoading(true);
    ApiRequest({
      method: "POST",
      url: "/subscriptions/deleteProduct",
      body: { 
        subscription_id,
        subscription_product_id: activeProduct.subscription_product_id 
      },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar("Product deleted successfully", { variant: "success" });
          fetchSubscriptionDetails();
        } else {
          enqueueSnackbar(response.message || "Failed to delete product", { variant: "error" });
        }
        setOpenAlert(false);
      },
    });
  };

  const handleOpenDialog = () => {
    setIsEditing(false);
    setActiveProduct(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setActiveProduct(null);
  };

  const handleProductAdded = () => {
    fetchSubscriptionDetails();
  };

  // Utilidad para formatear ssl_exp_date a MM / YYYY
  function formatExpDate(expDate: string | number | undefined): string {
    if (!expDate) return "MM / YYYY";
    let str = String(expDate).replace(/\D/g, "");
    if (str.length === 3) {
      // Ejemplo: 129 -> 01 / 2029
      const mm = `0${str[0]}`;
      const yy = str.slice(1, 3);
      return `${mm} / 20${yy}`;
    }
    if (str.length === 4) {
      // Ejemplo: 1126 -> 11 / 2026
      const mm = str.slice(0, 2);
      const yy = str.slice(2, 4);
      return `${mm} / 20${yy}`;
    }
    return str;
  }

  const getInvoice = (subscription_id:any, subscription_product_id:any, subscription_payment_id:any) => {
    setLoadingInvoice(subscription_product_id); // Set loading state
    ApiRequest({
      method: "get",
      url: "/subscription/getInvoice",
      query: { 
        subscription_id,
        subscription_product_id,
        subscription_payment_id
      },
      setResponse: (response: any) => {
        setLoadingInvoice(null); // Clear loading state
        if (response.status === "success") {
          // Create a hidden anchor element for file download
          const link = document.createElement("a");
          link.href = response.fileUrl;
          link.setAttribute("download", `invoice_${subscription_product_id}.pdf`);
          // Set target to _blank as a fallback in case download attribute isn't supported
          link.setAttribute("target", "_blank");
          // Make the element invisible
          link.style.display = "none";
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          // Small timeout to ensure download begins before removing the element
          setTimeout(() => {
            document.body.removeChild(link);
          }, 100);
          enqueueSnackbar("Invoice downloaded successfully", { variant: "success" });
        } else {
          enqueueSnackbar("Failed to fetch invoice", { variant: "error" });
        }
      },
    });
  }

  const sendInvoices = () => {
    setSendingMultipleInvoices(true);
    ApiRequest({
      method: "post",
      url: "/subscription/sendInvoices",
      body: { 
        payments: selectedPayments
      },
      setResponse: (response: any) => {
        setSendingMultipleInvoices(false);
        if (response.status === "success") {
          enqueueSnackbar("Invoices sent successfully", { variant: "success" });
          setSelectedPayments([]);
        } else {
          enqueueSnackbar(response.message || "Failed to send invoices", { variant: "error" });
        }
      },
    });
  }

  const updateSubscriptionStatus = (newStatus: string) => {
    setLoading(true);
    ApiRequest({
      method: "POST",
      url: "/subscriptions/updateStatus",
      body: { 
        subscription_id,
        status: newStatus
      },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar("Subscription status updated successfully", { variant: "success" });
        } else {
          enqueueSnackbar(response.message || "Failed to update subscription status", { variant: "error" });
        }
      },
    });
  };

  // Function to update input width based on content
  const updateInputWidth = (input: HTMLInputElement) => {
    if (!input) return;
    const value = input.value || input.placeholder || '';
    
    // Create a temporary element to measure the actual text width
    const temp = document.createElement('span');
    temp.style.visibility = 'hidden';
    temp.style.position = 'absolute';
    temp.style.whiteSpace = 'pre';
    temp.style.fontSize = getComputedStyle(input).fontSize;
    temp.style.fontFamily = getComputedStyle(input).fontFamily;
    temp.style.fontWeight = getComputedStyle(input).fontWeight;
    temp.textContent = value || input.placeholder || 'A';
    
    document.body.appendChild(temp);
    const textWidth = temp.getBoundingClientRect().width;
    document.body.removeChild(temp);
    
    // Add some padding (20px) to prevent text cutoff
    const newWidth = Math.max(textWidth + 20, 60); // minimum 60px
    input.style.width = `${newWidth}px`;
  };

  // Update input widths when values change
  useEffect(() => {
    if (editStatus) {
      if (firstNameRef.current) updateInputWidth(firstNameRef.current);
      if (lastNameRef.current) updateInputWidth(lastNameRef.current);
      if (emailRef.current) updateInputWidth(emailRef.current);
    }
  }, [subscription.client_name, subscription.client_lastname, subscription.email, editStatus]);

  // Function to format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  // Get the latest next payment date from all products
  const getLatestPaymentDate = () => {
    if (products.length === 0) return '';
    return products.reduce((latest, product) => {
      const currentDate = new Date(product.next_payment_date);
      const latestDate = new Date(latest);
      return currentDate > latestDate ? product.next_payment_date : latest;
    }, products[0].next_payment_date);
  };

  return (
    <div className="ModuleContainer">
      <div className="ModuleHeader">
        <div className="ComponentTitle">
          <h1>Subscription</h1>
        </div>
        <div className="ModuleActions">
          
        </div>

        <div className="subscription-flex-row">
          <div className="breadcrumbs-container">
            <div className="breadcrumb-item">
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/subscriptions");
                }}
                className="breadcrumb-link"
              >
                Subscriptions
              </a>
            </div>
            <div className="breadcrumb-separator"> <SlArrowRight size={12} /> </div>
            <div className="breadcrumb-item">
              {subscription.client_name || ''} {subscription.client_lastname || ''}
            </div>
          </div>

          <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
            {subscription.self_serve_link && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  style={{ 
                    padding: '4px 10px',
                    fontSize: '14px',
                  }}
                  onClick={() => window.open(subscription.self_serve_link, '_blank')}
                >
                  View Client Self Serve Window
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  style={{ 
                    padding: '4px 10px',
                    fontSize: '14px',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(subscription.self_serve_link);
                    enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
                  }}
                > 
                  Copy
                </Button>
              </>
            )}
          </div>
        </div>

      </div>

      <Divider style={{ borderColor: "#ECEBFF", margin: "20px 0" }} />

      <div className="ModuleContent">
        <div className="subscription_editor_content">
          <div className="subscription_flex">
            <div className="subscription_left">
              <div className="subscription_section">
                <h2>Subscription</h2>
                <TableContainer className="table_1">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell></TableCell>
                        <TableCell>Next billing date</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product, i) => (
                        <TableRow key={i}>
                          <TableCell>{product.product_name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={(product.status_text || 'Active').charAt(0).toUpperCase() + (product.status_text || 'Active').slice(1)} 
                              style={{ 
                                backgroundColor: product.status_id == 1 ? "#ADFFB2" : product.status_id == 3 ? "#FFADAD" : "#F5F5F5", 
                                color: product.status_id == 1 ? "#417029" : "#424242", 
                                borderRadius: "5px" 
                              }} 
                            />
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{product.next_payment_date}</span>
                              {product.scheduled_next_payment_date && (
                                <Tooltip 
                                  title={`After the next payment, the billing cycle will change to ${product.frequency}. Next payment with new cycle: ${product.scheduled_next_payment_date}`}
                                  arrow
                                  placement="top"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: 'rgba(121, 99, 255, 0.9)',
                                        color: 'white',
                                        '& .MuiTooltip-arrow': {
                                          color: 'rgba(121, 99, 255, 0.9)',
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <span style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}>
                                    <CiCircleAlert size={16} color="#7963FF" />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{product.frequency}</TableCell>
                          <TableCell>${formatCanadianPrice(product.total_product_price)}</TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(event) => handleMenuOpen(event, product)}>
                              <MoreHorizIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <a 
                  onClick={handleOpenDialog}
                  className="addProductButton"
                >
                  Add Subscription
                </a>
              </div>
            
              <Divider style={{ borderColor: "#ECEBFF"}} />

              <div className="subscription_section">
                <h2>Billing History</h2>
                <TableContainer className="table_1">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <div onClick={handleSelectAll} style={{ cursor: 'pointer' }} className="td_checkbox">
                            {payments.length > 0 && selectedPayments.length === payments.length ? (
                              <FaSquareCheck size={20} color="#7963FF" />
                            ) : (
                              <FaRegSquare size={20} color="#000" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Invoice</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment, i) => (
                        <TableRow key={i}>
                          <TableCell className="td_checkbox" padding="checkbox">
                            <div 
                              onClick={() => handleSelectOne(payment)} 
                              style={{ cursor: 'pointer' }}
                              className="td_checkbox"
                            >
                              {selectedPayments.some(
                                item => item.subscription_id === subscription_id && 
                                      item.subscription_product_id === payment.product_ids[0] &&
                                      item.subscription_payment_id === payment.subscription_payment_id
                              ) ? (
                                <FaSquareCheck size={20} color="#7963FF"/>
                              ) : (
                                <FaRegSquare size={20} />
                              )}
                            </div>
                          </TableCell>
                          <TableCell># {payment.subscription_payment_id}</TableCell>
                          <TableCell>{payment.payment_date}</TableCell>
                          <TableCell>${formatCanadianPrice(payment.payment_amount)}</TableCell>
                          <TableCell>
                            <Chip label="● Paid" style={{ backgroundColor: "#ADFFB2", color: "#417029", borderRadius: "5px" }} />
                          </TableCell>
                          <TableCell>
                            <LoadingButton
                              variant="outlined"
                              onClick={() => getInvoice(subscription_id, payment.product_ids[0], payment.subscription_payment_id)}
                              className="invoiceButton"
                              loading={loadingInvoice === payment.product_ids[0]}
                            >
                              Invoice
                            </LoadingButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {selectedPayments.length > 0 && (
                  <a 
                    onClick={sendingMultipleInvoices ? undefined : sendInvoices}
                    className="addProductButton"
                    style={{ 
                      marginTop: "15px", 
                      cursor: sendingMultipleInvoices ? "not-allowed" : "pointer",
                      opacity: sendingMultipleInvoices ? 0.7 : 1
                    }}
                  >
                    {sendingMultipleInvoices ? "Sending..." : `Send Invoices (${selectedPayments.length})`}
                  </a>
                )}
              </div>

              <Divider style={{ borderColor: "#ECEBFF", margin: "20px 0" }} />
            
              {/* Payment Method Section */}
              <div className="subscription_section">
                <h2>Payment Method</h2>

                <div className="payment-headers">
                    <span>Card</span>
                    <span>Expires</span>
                </div>
                <div className="payment-details">
                  <div className="payment-details-section">
                    <span>{subscription.payment_data?.ssl_card_short_description || "Card"} ending in {subscription.payment_data?.ssl_card_number?.slice(-4) || "****"}</span>
                   
                    <div className="payment-details-card-info">
                      <span>{subscription.name_on_card}</span>
                      <span>{subscription.city_name}, {subscription.region_code}, {subscription.postal_code}, {subscription.country_code}</span>
                    </div>
                  </div>

                  <Divider orientation="vertical" flexItem style={{ borderColor: "#ECEBFF"}} />

                  <div className="payment-details-section">
                    <p>{formatExpDate(subscription.payment_data?.ssl_exp_date)}</p>
                  </div>
                </div>

                <Button
                  variant="contained"
                  className="welcome_button"
                  onClick={() => setOpenCreditCard(true)}
                  >
                    Update Payment Method
                  </Button>
                
              </div>

              <Divider style={{ borderColor: "#ECEBFF"}} />

              <EventLogs 
                key={eventsKey}
                url="/subscription/eventList" 
                queryParams={{ subscription_id: subscription_id || "" }} 
                emptyMessage="No events recorded yet for this subscription."
              />
            </div>

            <div className="subscription_right">
              <div className="subscription_product_summary">

                
                              
                <div className="summary_container">

                  
                  <div className="summary_header">
                    <h3 className=''>Customer</h3>
                    {editStatus ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        
                        <IoMdClose 
                          color="#F44336" 
                          size={20} 
                          onClick={handleCancelEdit} 
                          style={{ cursor: 'pointer' }} 
                        />
                        <IoMdCheckmark 
                          color="#4CAF50" 
                          size={20} 
                          onClick={() => {
                            handleUpdateClientInfo();
                            setEditStatus(false);
                          }} 
                          style={{ cursor: 'pointer' }} 
                        />
                      </div>
                    ) : (
                      <HiOutlinePencilSquare 
                        color="#7963FF" 
                        size={18} 
                        onClick={() => setEditStatus(!editStatus)} 
                        style={{ cursor: 'pointer' }} 
                      />
                    )}
                  </div>

                  <div className="summary_item">
                    <div>
                      {editStatus ? (
                        <>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <input
                            ref={firstNameRef}
                            value={subscription.client_name || ''}
                            onChange={(e) => {
                              setSubscription((prev: any) => ({ ...prev, client_name: e.target.value }));
                              updateInputWidth(e.target);
                            }}
                            placeholder="First name"
                            className="purple_text"
                            style={{
                              border: 'none',
                              outline: 'none',
                              background: 'transparent',
                              fontSize: 'inherit',
                              fontFamily: 'inherit',
                              padding: 0,
                              margin: 0,
                              width: 'auto',
                              minWidth: '60px',
                              maxWidth: 'none',
                              lineHeight: '0.2'
                            }}
                          />
                          <input
                            ref={lastNameRef}
                            value={subscription.client_lastname || ''}
                            onChange={(e) => {
                              setSubscription((prev: any) => ({ ...prev, client_lastname: e.target.value }));
                              updateInputWidth(e.target);
                            }}
                            placeholder="Last name"
                            className="purple_text"
                            style={{
                              border: 'none',
                              outline: 'none',
                              background: 'transparent',
                              fontSize: 'inherit',
                              fontFamily: 'inherit',
                              padding: 0,
                              margin: 0,
                              width: 'auto',
                              minWidth: '60px',
                              maxWidth: 'none',
                              lineHeight: '0.2'
                            }}
                          />
                        </div>
                          <span className="gray_text">{products.length} Subscriptions</span>
                        </>
                      ) : (
                        <>
                          <span className="purple_text">{subscription.client_name} {subscription.client_lastname}</span>
                          <span className="gray_text">{products.length} Subscriptions</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="summary_item">
                    <div>
                      <h3 className=''>Contact Information</h3>
                      {editStatus ? (
                        <input
                          ref={emailRef}
                          type="email"
                          value={subscription.email || ''}
                          onChange={(e) => {
                            setSubscription((prev: any) => ({ ...prev, email: e.target.value }));
                            updateInputWidth(e.target);
                          }}
                          placeholder="Email address"
                          className="purple_text"
                          style={{
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            padding: 0,
                            margin: 0,
                            width: 'auto',
                            minWidth: '120px',
                            maxWidth: 'none',
                            lineHeight: '0.2'
                          }}
                        />
                      ) : (
                        <span className="purple_text">{subscription.email}</span>
                      )}
                    </div>
                  </div>

                  <div className="summary_item">
                    <div>
                      <h3 className=''>Account ID</h3>
                      <span className="black_text">{subscription.subscription_id}</span>
                    </div>
                  </div>

                </div>

                
              </div>
              

              <div className="cancel_subscription_section">
                {subscription.status_id === 3 ? (
                  <>
                    {subscription.show_cancellation_message && (
                      <>
                        <p style={{ 
                          fontSize: '14px', 
                          color: '#666666', 
                          marginBottom: '10px'
                        }}>
                          Subscription will be canceled on {formatDate(subscription.latest_next_payment_date)}
                        </p>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        style={{ 
                          padding: '4px 10px',
                          fontSize: '14px',
                        }}
                        onClick={() => setOpenResumeDialog(true)}
                      >
                        Resubscribe
                      </Button>
                    </>
                    )}
                    {subscription.cancellation_completed && (
                      <p style={{ 
                        fontSize: '14px', 
                        color: '#666666', 
                        marginBottom: '10px'
                      }}>
                        Subscription has been cancelled since {formatDate(subscription.latest_next_payment_date)}
                      </p>
                    )}
                  </>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    style={{ 
                      backgroundColor: '#D52626', 
                      color: 'white',
                      padding: '4px 10px',
                      fontSize: '14px',
                    }}
                    onClick={() => setOpenCancelDialog(true)}
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>

            </div>

          </div>

        </div>
        
        
      </div>

      {/* Add/Edit Subscription Product Dialog */}
      <AddProduct
        open={dialogOpen}
        onClose={handleCloseDialog}
        subscription_id={subscription_id || ""}
        onProductAdded={handleProductAdded}
        isEditing={isEditing}
        productData={activeProduct}
        subscription={subscription}
      />

      {/* Menu for product actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditProduct}>
          <EditIcon fontSize="small" style={{ marginRight: "8px" }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeletePrompt}>
          <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: "8px" }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Confirmation dialog for product deletion */}
      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="Are you sure you want to delete this product from the subscription?"
        send={handleDeleteProduct}
      />

      <AddCreditCard
        open={openCreditCard}
        setOpen={setOpenCreditCard}
        subscription={subscription}
        onSuccess={fetchSubscriptionDetails}
      />

      <CancelSubscriptionDialog
        open={openCancelDialog}
        setOpen={setOpenCancelDialog}
        loading={cancelLoading}
        subscription_id={subscription_id}
        next_billing_date={getLatestPaymentDate()}
        loadSubscriptionDetails={handleSubscriptionUpdate}
      />

      <ResumeSubscriptionDialog
        open={openResumeDialog}
        setOpen={setOpenResumeDialog}
        loading={resumeLoading}
        subscription_id={subscription_id}
        next_billing_date={getLatestPaymentDate()}
        loadSubscriptionDetails={handleSubscriptionUpdate}
      />
    </div>
  );
}
